<template>
  <div v-if="isReportMode">
    <ReportItemContainer>
      <template #question>{{ question.text }}</template>
      <template #answer>
        <div v-if="showAnswer">
          <div v-for="item in checkboxItems" :key="item.label">
            <p v-if="item.id != 'None of the above'">
              <span>{{ item.label }}: </span>{{ getAnswerText(item.checked) }}
            </p>
          </div>
        </div>
      </template>
    </ReportItemContainer>
  </div>
  <div v-else class="mb-8">
    <p class="mb-4">{{ question.text }}</p>
    <div v-for="checkbox in checkboxItems" :key="checkbox.label">
      <v-row>
        <v-col cols="12" sm="6" md="12">
          <PCheckBox
            :label="checkbox.label"
            :value="checkbox.checked"
            :disabled="isReadOnlyMode"
            @value-changed="(val) => checkboxSelected(checkbox as CheckboxModel, val)"
          />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  CheckboxModel,
  AnswersCheckboxModel,
} from "@/models/questions/checkbox";
import { computed, ref } from "vue";
import ReportItemContainer from "@/components/containers/ReportItemContainer.vue";
import type { QuestionModelDto } from "@/models/dto/questionModelDto";
import type { ItemTextDto } from "@/models/dto/questionDto";
import { useSelectedQuestionnaire } from "@/stores/selectedQuestionnare";
import PCheckBox from "@/components/custom/PCheckBox.vue";

const props = withDefaults(
  defineProps<{
    questionModel: QuestionModelDto;
    answer?: AnswersCheckboxModel[] | null;
    isReportMode?: boolean;
    isReadOnlyMode?: boolean;
  }>(),
  {
    isReportMode: false,
    isReadOnlyMode: false,
  }
);

const showAnswer = computed(() => {
  if (props.answer === null || props.answer === undefined) {
    return false;
  } else {
    var anyTrue = false;
    checkboxItems.value.forEach((c) => {
      if (c.checked) {
        anyTrue = true;
      }
    });
    return anyTrue;
  }
});

const emit =
  defineEmits<(e: "qEvent", newValue: AnswersCheckboxModel[]) => void>();

const selectedQuestionnaireStore = useSelectedQuestionnaire();

const question = computed(() => props.questionModel.question);
const checkboxItems = ref(getCheckboxItems());

const checkBoxAnswerDisplayText =
  selectedQuestionnaireStore.getCommonItemsForQuestionType(
    props.questionModel.question.type
  );

function emitNewCheckBoxesValue(): void {
  const answerModelItems = checkboxItems.value.map((x) => x.toAnswerModel());
  emit("qEvent", answerModelItems);
}

function checkboxSelected(item: CheckboxModel, state: boolean) {
  if (state) {
    toggleNoneOfTheAbove(item);
  }
  item.checked = state;
  emitNewCheckBoxesValue();
}

function toggleNoneOfTheAbove(item: CheckboxModel) {
  if (item.id == "None of the above") {
    checkboxItems.value.forEach((element) => {
      if (element.id != "None of the above") {
        element.checked = false;
      }
    });
  } else {
    checkboxItems.value.forEach((element) => {
      if (element.id == "None of the above") {
        element.checked = false;
      }
    });
  }
}

function getCheckboxItems(): CheckboxModel[] {
  if (question.value.items == undefined || question.value.items.length == 0) {
    return new Array<CheckboxModel>();
  }

  const answersExist = props.answer != undefined && props.answer.length > 0;

  if (!answersExist) {
    return question.value.items!.map(
      (item: ItemTextDto) => new CheckboxModel(item.id, item.text)
    );
  }

  return question.value.items!.map((item: ItemTextDto) => {
    const model = new CheckboxModel(item.id, item.text);
    const answer = props.answer!.find(
      (a: AnswersCheckboxModel) => a.label === item.id
    );
    if (answer) {
      model.checked = answer.checked;
    }
    return model;
  });
}

function getAnswerText(answerId: boolean): string {
  const entry = checkBoxAnswerDisplayText.find(
    (x) => x.id == answerId.toString()
  );
  return entry?.text ?? "";
}
</script>
